import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import { useToast } from '@/contexts/ToastContext';

interface Device {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  volume_percent: number | null;
}

interface DeviceSelectorProps {
  onDeviceReady?: () => void;
}

/**
 * DeviceSelector Component - Compact dropdown version
 * Polls for 5 seconds on mount, then stops until refresh
 */
export const DeviceSelector: React.FC<DeviceSelectorProps> = ({ onDeviceReady }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [activeDevice, setActiveDevice] = useState<Device | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(true);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const { success, error: showError } = useToast();

  const fetchDevices = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/spotify/devices');
      if (!response.ok) throw new Error('Failed to fetch devices');

      const data = await response.json();
      const deviceList = data.devices || [];
      setDevices(deviceList);

      const active = deviceList.find((d: Device) => d.is_active) || null;
      setActiveDevice(active);

      // If we found a device, notify parent and stop searching
      if (deviceList.length > 0) {
        setIsSearching(false);
        if (onDeviceReady) onDeviceReady();
      }

      return deviceList.length > 0;
    } catch (err) {
      console.error('Error fetching devices:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [onDeviceReady]);

  // Initial polling for 5 seconds
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let pollTimeout: ReturnType<typeof setTimeout> | null = null;

    const startPolling = async () => {
      setIsSearching(true);
      setSearchCount(0);

      // Poll every 1 second for 5 seconds
      pollInterval = setInterval(async () => {
        setSearchCount(prev => prev + 1);
        const found = await fetchDevices();
        if (found && pollInterval) {
          clearInterval(pollInterval);
          setIsSearching(false);
        }
      }, 1000);

      // Stop after 5 seconds
      pollTimeout = setTimeout(() => {
        if (pollInterval) clearInterval(pollInterval);
        setIsSearching(false);
      }, 5000);
    };

    startPolling();

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (pollTimeout) clearTimeout(pollTimeout);
    };
  }, [fetchDevices]);

  const handleRefresh = async () => {
    setIsSearching(true);
    setSearchCount(0);

    // Poll every 1 second for 5 seconds
    const pollInterval = setInterval(async () => {
      setSearchCount(prev => prev + 1);
      const found = await fetchDevices();
      if (found) {
        clearInterval(pollInterval);
        setIsSearching(false);
      }
    }, 1000);

    // Stop after 5 seconds
    setTimeout(() => {
      clearInterval(pollInterval);
      setIsSearching(false);
    }, 5000);
  };

  const handleDeviceSelect = async (device: Device) => {
    if (device.is_active || isTransferring) {
      setIsOpen(false);
      return;
    }

    setIsTransferring(true);
    try {
      const response = await authenticatedFetch('/api/spotify/player/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: device.id, play: true }),
      });

      if (!response.ok) throw new Error('Failed to transfer playback');

      await fetchDevices();
      setIsOpen(false);
      success(`Playing on ${device.name}`);
    } catch (err) {
      console.error('Failed to transfer playback:', err);
      showError('Failed to transfer playback. Is Spotify open?');
    } finally {
      setIsTransferring(false);
    }
  };

  const getDeviceIcon = (type: string): string => {
    const iconMap: Record<string, string> = {
      Computer: '💻',
      Smartphone: '📱',
      Speaker: '🔊',
      TV: '📺',
      'Game Console': '🎮',
    };
    return iconMap[type] || '🎵';
  };

  // No device found state - prominent warning
  if (!isLoading && devices.length === 0 && !isSearching) {
    return (
      <div className="bg-yellow-500/10 border-2 border-yellow-500/40 rounded-lg p-4 animate-fade-in">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1">
            <span className="text-2xl">🔊</span>
            <div>
              <p className="text-yellow-400 font-medium">No Spotify Device Found</p>
              <p className="text-yellow-400/70 text-sm">Open Spotify on your phone, computer, or speaker</p>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <a
              href="https://open.spotify.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 sm:flex-none px-4 py-2 bg-spotify-green text-black rounded-lg text-sm font-medium hover:bg-spotify-green-light transition-colors text-center"
            >
              Open Spotify
            </a>
            <button
              onClick={handleRefresh}
              className="flex-1 sm:flex-none px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm font-medium hover:bg-yellow-500/30 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Searching state
  if (isSearching) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-spotify-green"></div>
        <span className="text-sm text-gray-400">
          Searching for devices... ({searchCount}/5)
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        {/* Dropdown Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
        >
          {activeDevice ? (
            <>
              <span className="text-lg">{getDeviceIcon(activeDevice.type)}</span>
              <span className="text-sm font-medium text-white truncate flex-1 text-left">
                {activeDevice.name}
              </span>
              <span className="w-2 h-2 bg-spotify-green rounded-full"></span>
            </>
          ) : (
            <>
              <span className="text-lg">🎵</span>
              <span className="text-sm text-gray-400 flex-1 text-left">
                Select device
              </span>
            </>
          )}
          <span className="text-gray-400 text-xs">{isOpen ? '▲' : '▼'}</span>
        </button>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={isSearching}
          className="p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          title="Refresh devices"
        >
          <span className={`text-sm ${isSearching ? 'animate-spin' : ''}`}>🔄</span>
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-50 overflow-hidden">
          {devices.length === 0 ? (
            <div className="p-3 text-sm text-gray-400">
              No devices found. Open Spotify on a device.
            </div>
          ) : (
            devices.map((device) => (
              <button
                key={device.id}
                onClick={() => handleDeviceSelect(device)}
                disabled={isTransferring}
                className={`w-full flex items-center gap-3 p-3 hover:bg-gray-800 transition-colors ${
                  device.is_active ? 'bg-spotify-green/20' : ''
                } ${isTransferring ? 'opacity-50' : ''}`}
              >
                <span className="text-lg">{getDeviceIcon(device.type)}</span>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">{device.name}</p>
                  <p className="text-xs text-gray-400">{device.type}</p>
                </div>
                {device.is_active && (
                  <span className="text-xs text-spotify-green font-semibold">Active</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceSelector;
